import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { TextButton, Box, Typography, CircularProgress, useMediaQuery } from 'bonfire-kindling';

import { ToastContext } from 'contexts';
import { PROJECTS_SEARCH } from 'constants/apis';
import { SORT_BY, PROJECT_STATUS, PROJECT_SOURCE } from 'constants/opportunities';
import axios from 'helpers/axios';
import DashboardOverviewContentCard from '../DashboardOverviewContentCard';
import RecommendedOpportunity from './RecommendedOpportunity';

export const DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY = [
  'projects',
  'search',
  'recommended',
  'dashboard',
];

// This query in particular isn't paginated, we only care about
// fetching it once.
const requestConfig = {
  body: {
    type: 'recommended',
    statuses: [PROJECT_STATUS.open],
    sources: [PROJECT_SOURCE.bonfire, PROJECT_SOURCE.external],
    projectTypes: [],
    locations: [],
  },
  options: {
    params: {
      limit: 5,
      page: 1,
      sort: SORT_BY.mostRecent,
      direction: 'desc',
    },
  },
};

const RecommendedOpportunities = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const { triggerToast } = useContext(ToastContext);
  const { isLoading, data: projects } = useQuery(
    DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY,
    () =>
      axios
        .post(PROJECTS_SEARCH, requestConfig.body, requestConfig.options)
        .then(resp => resp.data),
    {
      onError: () => triggerToast({ message: t('generic_error') }),
    }
  );

  return (
    <DashboardOverviewContentCard
      title={t('dashboard.overview.recommended_opportunities.title')}
      iconName="star"
      link={
        <TextButton
          component={RouterLink}
          to="/opportunities/recommended"
          label={
            isMobile
              ? t('dashboard.overview.recommended_opportunities.view')
              : t('dashboard.overview.recommended_opportunities.view_all')
          }
          variant="contained"
        />
      }
    >
      {isLoading ? (
        <Box sx={{ p: 3 }}>
          <CircularProgress
            aria-label={t('components.loading_icon')}
            sx={{ display: 'block', margin: 'auto' }}
          />
        </Box>
      ) : projects?.length ? (
        <Box
          sx={{
            '& > * + *': {
              mt: 2,
            },
            px: 3,
            listStyleType: 'none',
          }}
          component="ul"
        >
          {projects.map(project => (
            <RecommendedOpportunity {...project} />
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" paragraph>
              {t('dashboard.overview.recommended_opportunities.empty.title')}
            </Typography>
            <Typography variant="h4">
              {t('dashboard.overview.recommended_opportunities.empty.subtitle')}:
            </Typography>
            <Box component="ul" sx={{ textAlign: 'left', maxWidth: '31rem' }}>
              <Box component="li">
                <Typography>
                  <Trans i18nKey="dashboard.overview.recommended_opportunities.empty.first_tip">
                    Broaden your
                    <RouterLink to="/settings/commodity-codes">commodity code</RouterLink> or
                    <RouterLink to="/settings/keywords">keyword</RouterLink> criteria.
                  </Trans>
                </Typography>
              </Box>
              <Box component="li">
                <Typography>
                  {t('dashboard.overview.recommended_opportunities.empty.second_tip')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </DashboardOverviewContentCard>
  );
};

export default RecommendedOpportunities;
